import { useContext, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { GlobalContext } from '../../../library/Core/GlobalContext';
import CommitmentService from '../commitment/CommitmentService';

interface Props {
    instituteId: string | number;
    initialCommitmentPaths?: string[];
}

interface PathwayGoalRow {
    code: string;
    value: string;
    flexCol?: { icon?: string; color_primary?: string; color_light?: string } | string;
    displayOrder?: number;
}

const MIN_SELECTIONS = 1;
const MAX_SELECTIONS = 3;

const parseFlex = (raw: PathwayGoalRow['flexCol']) => {
    if (!raw) return {};
    if (typeof raw === 'object') return raw as Record<string, string>;
    try {
        return JSON.parse(raw) as Record<string, string>;
    } catch {
        return {};
    }
};

const CommitmentPathsForm = ({ instituteId, initialCommitmentPaths = [] }: Props) => {
    const { globalState } = useContext(GlobalContext);

    const goals: PathwayGoalRow[] = useMemo(() => {
        const map: any = globalState?.domain;
        const rows = map?.get ? map.get('PATHWAY_GOAL') : null;
        if (!rows || !Array.isArray(rows)) return [];
        return [...rows].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    }, [globalState]);

    const [selected, setSelected] = useState<string[]>(initialCommitmentPaths || []);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setSelected(initialCommitmentPaths || []);
    }, [initialCommitmentPaths]);

    const toggle = (code: string) => {
        setSelected((prev) => {
            if (prev.includes(code)) {
                return prev.filter((c) => c !== code);
            }
            // Cap at MAX_SELECTIONS — silently no-op once the cap is hit
            // (a toast on every blocked click would be noisy). The helper
            // text in the JSX surfaces "Max N reached" instead.
            if (prev.length >= MAX_SELECTIONS) return prev;
            return [...prev, code];
        });
    };

    const onSave = async () => {
        if (saving) return;
        if (selected.length < MIN_SELECTIONS) {
            Swal.fire({
                title: `Pick at least ${MIN_SELECTIONS} path`,
                text: `Select ${MIN_SELECTIONS - selected.length} more before saving — every institute needs a minimum of ${MIN_SELECTIONS} commitment path.`,
                icon: 'warning',
                confirmButtonText: 'OK',
            });
            return;
        }
        if (selected.length > MAX_SELECTIONS) {
            Swal.fire({
                title: `Maximum ${MAX_SELECTIONS} paths allowed`,
                text: `Unselect ${selected.length - MAX_SELECTIONS} to bring the total down to ${MAX_SELECTIONS}.`,
                icon: 'warning',
                confirmButtonText: 'OK',
            });
            return;
        }
        setSaving(true);
        try {
            const res = await CommitmentService.saveCommitmentPaths(instituteId, selected);
            if (res.success) {
                Swal.fire({
                    title: 'Success!',
                    text: res.message || 'Commitment Paths saved',
                    icon: 'success',
                    showConfirmButton: false,
                    timer: 1200,
                    timerProgressBar: true,
                });
            } else {
                Swal.fire({ title: 'Error!', text: res.message || 'Save failed', icon: 'error', confirmButtonText: 'Close' });
            }
        } catch (err: any) {
            Swal.fire({
                title: 'Error!',
                text: err?.message || 'Save failed',
                icon: 'error',
                confirmButtonText: 'Close',
            });
        } finally {
            setSaving(false);
        }
    };

    if (!goals.length) {
        return (
            <div className="p-4 text-center text-secondary">
                <p className="mb-0">
                    PATHWAY_GOAL domain rows are not loaded. Verify the domain snapshot job has run and the 6 PATHWAY_GOAL rows exist in <code>domain_data</code>.
                </p>
            </div>
        );
    }

    return (
        <div className="lstCustForm w-100 formrowpx mt-2 max-700 mx-auto">
            <div className="row g-4">
                <div className="col-12">
                    <label className="form-label font-16 font-500 mb-3">Commitment Paths</label>
                    <p className="text-secondary mb-3" style={{ fontSize: 13 }}>
                        Select which commitment goals success managers can capture for this institute's alumni. Only selected paths will appear on user-facing screens.{' '}
                        <span className="fw-semibold" style={{ color: '#166534' }}>
                            Min {MIN_SELECTIONS}, Max {MAX_SELECTIONS}.
                        </span>
                    </p>
                    <div className="d-flex flex-column gap-2">
                        {goals.map((g) => {
                            const flex = parseFlex(g.flexCol);
                            const checked = selected.includes(g.code);
                            // Once MAX_SELECTIONS is reached, the remaining
                            // unchecked rows are disabled so the user
                            // visually understands the cap (instead of
                            // silently no-op clicks).
                            const disabled =
                                !checked && selected.length >= MAX_SELECTIONS;
                            return (
                                <label
                                    key={g.code}
                                    className="d-flex align-items-center gap-3 p-3 border rounded-3"
                                    style={{
                                        cursor: disabled ? 'not-allowed' : 'pointer',
                                        background: checked
                                            ? flex.color_light || '#f0fdf4'
                                            : '#fff',
                                        borderColor: checked
                                            ? flex.color_primary || '#166534'
                                            : '#e5e7eb',
                                        opacity: disabled ? 0.55 : 1,
                                    }}>
                                    <input
                                        type="checkbox"
                                        className="form-check-input m-0"
                                        checked={checked}
                                        disabled={disabled}
                                        onChange={() => toggle(g.code)}
                                    />
                                    <span
                                        className="badge rounded-pill px-2 py-1 fw-bold"
                                        style={{ background: flex.color_light || '#e5e7eb', color: flex.color_primary || '#475569', fontSize: 11 }}>
                                        {g.code}
                                    </span>
                                    <span className="fw-medium">{g.value}</span>
                                </label>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="d-flex justify-content-end align-items-center gap-3 mt-4 mb-4">
                {selected.length < MIN_SELECTIONS && (
                    <span className="text-secondary" style={{ fontSize: 12 }}>
                        Select at least {MIN_SELECTIONS} to enable Save
                    </span>
                )}
                {selected.length >= MAX_SELECTIONS && (
                    <span className="text-secondary" style={{ fontSize: 12 }}>
                        Max {MAX_SELECTIONS} reached — unselect one to swap
                    </span>
                )}
                <button
                    type="button"
                    className="btn btn-green text-white px-4"
                    onClick={onSave}
                    disabled={
                        saving ||
                        selected.length < MIN_SELECTIONS ||
                        selected.length > MAX_SELECTIONS
                    }
                    title={
                        selected.length < MIN_SELECTIONS
                            ? `Select at least ${MIN_SELECTIONS} commitment path`
                            : selected.length > MAX_SELECTIONS
                                ? `Maximum ${MAX_SELECTIONS} commitment paths allowed`
                                : undefined
                    }
                >
                    {saving ? 'Saving…' : 'Save'}
                </button>
            </div>
        </div>
    );
};

export default CommitmentPathsForm;
